import {
  Box,
  Flex,
  Text,
  Tooltip,
  useDisclosure,
  useTheme,
} from '@chakra-ui/react';
import KeyResultsBarChart from './KeyResultsBarChart';
import { KeyResultViewDetails } from './KeyResultViewDetails';
import { RightIcon } from '../icons';
import { splitTime } from '../shared/utils';
import { KeyResult } from './types';

interface KeyResultsDetailProps {
  data: KeyResult;
  scope: string;
  goalStartDate: string;
  goalEndDate: string;
}

export const KeyResultsDetail = ({
  data,
  scope,
  goalStartDate,
  goalEndDate,
}: KeyResultsDetailProps) => {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { colors } = useTheme();
  return (
    <Flex gap={'24px'}>
      <Box w={'65%'} h={'300px'}>
        <KeyResultsBarChart
          data={data?.data}
          targetValue={data?.targetValue}
          metricOperator={data?.metricOperator}
          unit={data?.unit}
          metricKey={data?.metricName}
          metricTitle={data?.metricTitle}
          scope={scope}
          keyResultTitle={data?.title}
        />
      </Box>
      <Flex
        maxW={'35%'}
        minW={'35%'}
        mt={'28px'}
        justifyContent={'space-between'}
        overflow={'hidden'}
      >
        <Flex
          gap={6}
          wrap={'wrap'}
          w={'50%'}
          direction={'column'}
          overflow={'hidden'}
        >
          <Flex
            gap={1}
            fontSize={'sm'}
            flexDirection={'column'}
            w={'100%'}
            overflow={'hidden'}
          >
            <Text fontWeight={'medium'} color={'text.gray.300'}>
              Metric
            </Text>
            <Text
              fontWeight={'normal'}
              color={'text.secondary2'}
              maxW={'90%'}
              isTruncated
            >
              {data?.metricTitle}
            </Text>
          </Flex>
          <Flex
            gap={1}
            fontSize={'sm'}
            flexDirection={'column'}
            w={'100%'}
            overflow={'hidden'}
          >
            <Text fontWeight={'medium'} color={'text.gray.300'}>
              Weightage
            </Text>
            <Text
              fontWeight={'normal'}
              color={'text.secondary2'}
              maxW={'90%'}
              isTruncated
            >
              {data?.weight}%
            </Text>
          </Flex>
          <Flex
            gap={1}
            fontSize={'sm'}
            flexDirection={'column'}
            w={'100%'}
            overflow={'hidden'}
          >
            <Text fontWeight={'medium'} color={'text.gray.300'}>
              Active since
            </Text>
            <Text
              fontWeight={'normal'}
              color={'text.secondary2'}
              maxW={'90%'}
              isTruncated
            >
              {data?.activeSince} days
            </Text>
          </Flex>
        </Flex>
        <Flex
          gap={6}
          wrap={'wrap'}
          w={'50%'}
          direction={'column'}
          overflow={'hidden'}
        >
          <Flex
            gap={1}
            fontSize={'sm'}
            flexDirection={'column'}
            w={'100%'}
            overflow={'hidden'}
          >
            <Text fontWeight={'medium'} color={'text.gray.300'}>
              Target goal
            </Text>
            <Tooltip
              label={`${
                data?.metricOperator === 'LESS_THAN'
                  ? '< '
                  : data?.metricOperator === 'GREATER_THAN'
                  ? '> '
                  : ''
              }${data?.targetValue} ${data?.unit}`}
              isDisabled={data?.unit === 'Minutes'}
            >
              <Text
                fontWeight={'normal'}
                color={'text.secondary2'}
                maxW={'90%'}
                isTruncated
              >
                {data?.metricOperator === 'LESS_THAN'
                  ? '< '
                  : data?.metricOperator === 'GREATER_THAN'
                  ? '> '
                  : ''}{' '}
                {data?.unit === 'Minutes'
                  ? splitTime((parseFloat(data?.targetValue || '0') || 0) / 60)
                  : data?.targetValue}{' '}
                {data?.unit !== 'Minutes' && (
                  <Text as={'span'} fontSize={'xs'}>
                    ({data?.unit})
                  </Text>
                )}
              </Text>
            </Tooltip>
          </Flex>
          <Flex
            gap={1}
            fontSize={'sm'}
            flexDirection={'column'}
            w={'100%'}
            overflow={'hidden'}
          >
            <Text fontWeight={'medium'} color={'text.gray.300'}>
              Created by
            </Text>
            <Tooltip label={data?.updatedBy}>
              <Text
                fontWeight={'normal'}
                color={'text.secondary2'}
                maxW={'90%'}
                isTruncated
              >
                {data?.updatedBy}
              </Text>
            </Tooltip>
          </Flex>
          {scope !== 'CONTRIBUTOR' && (
            <Flex
              align={'center'}
              gap={1}
              fontWeight={'medium'}
              color={'primary'}
              onClick={onOpen}
              fontSize={'sm'}
              cursor={'pointer'}
            >
              View Breakdown
              <RightIcon
                height="12px"
                width="12px"
                color={colors.primary}
                strokeWidth="3"
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      {isOpen && (
        <KeyResultViewDetails
          isOpen={isOpen}
          onClose={onClose}
          metricTitle={data?.metricTitle}
          metricKey={data?.metricName}
          goalStartDate={goalStartDate}
          goalEndDate={goalEndDate}
          scope={scope}
          keyResultTitle={data?.title}
        />
      )}
    </Flex>
  );
};
